import L from 'leaflet';
import { UrlState, constructUrl } from '../map/urlState';
import { BaseLayers } from '../map/layers';
import { Overlays } from '../map/overlays';
import { config } from '../config';
import { post } from '../utils/api';

export function initSettingsPanel(
    map: L.Map,
    baseLayers: BaseLayers,
    overlays: Overlays,
    mapFilterInput: HTMLInputElement,
    getCurrentUrlState: () => UrlState // Function to get the current state
) {
    // 1. Inject HTML
    const panelHtml = `
  <div id="settings-panel" style="display:none;">
    <h3>URL Generator for Training</h3>
    <div style="border:1px solid #333; border-radius:8px; padding:1em;">
        <div style="border:1px solid #333; border-radius:8px; padding: 0.5em; margin-bottom: 0.5em;">
            <label><input type="checkbox" value="Beaches" class="display-checkbox"> Beaches</label><br>
            <label><input type="checkbox" value="Bays" class="display-checkbox"> Bays</label><br>
            <label><input type="checkbox" value="Islands" class="display-checkbox"> Islands</label><br>
            <label><input type="checkbox" value="Others" class="display-checkbox"> Others</label><br>
            <label><input type="checkbox" value="Lights" class="display-checkbox"> Lights</label><br>
            <label><input type="checkbox" value="Howick" class="display-checkbox"> Howick PDF</label><br>
            <label><input type="checkbox" value="Maraetai" class="display-checkbox"> Maraetai PDF</label><br>
            <label><input type="checkbox" value="VHF" class="display-checkbox"> VHF</label><br>
            <p></p>
            Level:
            <label><input type="radio" name="level" value="1"> 1</label>
            <label><input type="radio" name="level" value="2"> 2</label>
            <label><input type="radio" name="level" value="3"> 3</label>
        <p/>
            <label for="unit-select">&nbsp;Unit:</label>
            <input list="unit-options" id="unit-select" class="display-select" placeholder="Type to search...">
            <datalist id="unit-options">
                <!-- Options will be populated from backend -->
            </datalist>
            <br>
        </div>
        
        <div style="border:1px solid #333; border-radius:8px; padding: 0.5em; margin-bottom: 0.5em;">
            <label><input type="checkbox" value="Open Maps" class="display-checkbox"> Open Maps</label><br>
            <label><input type="checkbox" value="LINZ Gazetteer" class="display-checkbox"> LINZ Gazetteer</label><br>
            <label><input type="checkbox" value="LINZ Lights" class="display-checkbox"> LINZ Lights</label><br>
            <label><input type="checkbox" value="Tangata Whenua" class="display-checkbox"> Tangata Whenua</label><br>
            <label><input type="checkbox" value="OSM Gazetteer" class="display-checkbox"> OSM Gazetteer</label><br>
            <label><input type="checkbox" value="Extra" class="display-checkbox"> Extra</label><br>
            <label><input type="checkbox" value="CG Units" class="display-checkbox"> CG Units</label><br>
            <label><input type="checkbox" value="Divider" class="display-checkbox"> Divider</label><br>
        </div>
        <div>
            <p>POI Filter :</p>
            <input type="text" id="filter-input" placeholder="Enter POI filter" style="width: 90%;">
            <p style="font-size: 0.8em; color: #666;">'ati' shows places with names like 'Matiatia'<br>
            '*chips' shows places with 'chips' anywhere in POI popup text</p>
        </div>
    </div>
    <div style="margin-top:1em;">
        <button id="apply-settings-btn">Go!</button>
        <button id="close-settings-btn">Close</button>
    </div>
</div>
    `;
    
    // Append to app container (or body)
    const appContainer = document.getElementById('app');
    if (appContainer) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = panelHtml;
        appContainer.appendChild(tempDiv.firstElementChild!);
    }

    // 2. Get References
    const settingsPanel = document.getElementById('settings-panel')!;
    const closeSettingsBtn = document.getElementById('close-settings-btn')!;
    const applySettingsBtn = document.getElementById('apply-settings-btn')!;
    const panelFilterInput = document.getElementById('filter-input') as HTMLInputElement;
    const unitInput = document.getElementById('unit-select') as HTMLInputElement;
    const unitDatalist = document.getElementById('unit-options') as HTMLDataListElement;

    // 3. Define Logic
    let unitsLoaded = false;

    async function fetchUnitData() {
        if (unitsLoaded) return;

        const data = await post<{ units?: any[] } | any[]>('/api/unit-data');
        if (!data) return;

        unitDatalist.innerHTML = ''; // Clear existing options
        
        // Handle { units: [...] } structure or direct array
        const units = Array.isArray(data) ? data : (data.units || []);

        if (Array.isArray(units)) {
            units.forEach((item: any) => {
                const option = document.createElement('option');
                const unitName = typeof item === 'string' ? item : item.name;
                option.value = unitName;
                unitDatalist.appendChild(option);
            });
        }
        
        unitsLoaded = true;
    }

    // Fetch units immediately on init
    fetchUnitData();

    function openSettingsPanel() {
        const currentState = getCurrentUrlState();

        // Sync the panel filter input with the main map filter input
        panelFilterInput.value = mapFilterInput.value;
        
        // Populate unit
        unitInput.value = currentState.unit || '';
        
        // Populate level
        if (currentState.level) {
            const levelRadio = document.querySelector(`input[name="level"][value="${currentState.level}"]`) as HTMLInputElement;
            if (levelRadio) levelRadio.checked = true;
        } else {
            // Clear radios if none set
            document.querySelectorAll('input[name="level"]').forEach((r: any) => r.checked = false);
        }
        
        // Populate checkboxes based on ACTIVE map layers
        document.querySelectorAll('.display-checkbox').forEach((cb: any) => {
            const layerName = cb.value;
            const isActive = overlays[layerName] && map.hasLayer(overlays[layerName]);
            cb.checked = isActive;
        });
        
        settingsPanel.style.display = 'block';
    }

    // 4. Attach Listeners
    map.getContainer().addEventListener('open-settings-panel', () => {
        if (settingsPanel.style.display === 'block') {
            settingsPanel.style.display = 'none';
        } else {
            map.getContainer().dispatchEvent(new CustomEvent('close-other-panels', { detail: 'settings-panel' }));
            openSettingsPanel();
        }
    });

    map.getContainer().addEventListener('close-other-panels', (e: any) => {
        if (e.detail !== 'settings-panel') {
            settingsPanel.style.display = 'none';
        }
    });

    closeSettingsBtn.addEventListener('click', () => {
        settingsPanel.style.display = 'none';
    });

    unitInput.addEventListener('change', (event) => {
        const inputValue = (event.target as HTMLInputElement).value;
        if (inputValue === '') return; // Allow clearing the input
    
        const options = Array.from(unitDatalist.options).map(opt => opt.value);
        const lowerCaseOptions = options.map(opt => opt.toLowerCase());
        const lowerCaseInputValue = inputValue.toLowerCase();
    
        const matchIndex = lowerCaseOptions.indexOf(lowerCaseInputValue);
    
        if (matchIndex > -1) {
            // A valid option was typed, possibly with different casing. Correct it.
            const correctValue = options[matchIndex];
            if (unitInput.value !== correctValue) {
                unitInput.value = correctValue;
            }
        } else {
            // Invalid value
            alert(`'${inputValue}' is not a valid unit. Please select a unit from the list.`);
            unitInput.value = '';
        }
    });

    applySettingsBtn.addEventListener('click', async () => {
        // Collect values
        const newFilter = panelFilterInput.value;
        const newUnit = unitInput.value === 'none' ? '' : unitInput.value;
        
        // Always set mode to training for generated URLs
        const newMode = 'training';

        const levelRadio = document.querySelector('input[name="level"]:checked') as HTMLInputElement;
        const newLevel = levelRadio ? levelRadio.value : '';
        
        const newOverlays: string[] = [];
        document.querySelectorAll('.display-checkbox:checked').forEach((cb: any) => {
            newOverlays.push(cb.value);
        });

        // Determine active base layer
        let activeBaseLayerName = '';
        map.eachLayer((layer) => {
            for (const [name, baseLayer] of Object.entries(baseLayers)) {
                if (layer === baseLayer) {
                    activeBaseLayerName = name;
                    break;
                }
            }
        });

        // Determine center for the new URL
        let newCenter = map.getCenter(); // Default to current map center
        
        if (newUnit) {
            const data = await post<{ features?: any[] }>('/api/poi-id', { id: newUnit });
            if (data && data.features && data.features.length > 0) {
                const geometry = data.features[0].geometry;
                if (geometry && geometry.type === 'Point' && geometry.coordinates) {
                    // GeoJSON is [lng, lat], Leaflet needs [lat, lng]
                    const [lng, lat] = geometry.coordinates;
                    newCenter = L.latLng(lat, lng);
                }
            }
        }

        // Construct the new URL
        const newUrl = constructUrl(
            activeBaseLayerName,
            newOverlays,
            newFilter,
            newUnit,
            newLevel,
            newMode,
            newCenter, // Use the potentially updated center
            map.getZoom()
        );

        // Open in new tab
        window.open(newUrl, '_blank');
        
        settingsPanel.style.display = 'none';
    });
}
